import { Icon } from "@mirage-web/icons";
import { Link, createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import { PermissionGuard } from "~/components/permission-guard";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { ChargeHistorySection } from "~/modules/credits/components/charge-history-section";
import { CreditGrantDialog } from "~/modules/credits/components/credit-grant-dialog";
import { lookupUserByIdQuery } from "~/modules/lookup/queries";

const querySchema = z.object({
  sheet: z.enum(["grant"]).optional(),
});

export const Route = createFileRoute("/_dash/charges/users/$userId")({
  component: RouteComponent,
  async loader({ params, context }) {
    const { queryClient } = context;

    if (!params.userId) {
      throw new Error("User ID is required");
    }

    queryClient.prefetchQuery(
      lookupUserByIdQuery.queryOptions({ pathParams: { userId: params.userId } })
    );
  },
  validateSearch: querySchema,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { userId } = Route.useParams();
  const search = Route.useSearch();

  const { data: user } = lookupUserByIdQuery.useSuspenseQuery({
    pathParams: { userId },
  });

  return (
    <PermissionGuard permission="users:view">
      <div className="space-y-6">
        {search.sheet == "grant" && (
          <CreditGrantDialog
            onClose={() => navigate({ search: (old) => ({ ...old, sheet: undefined }) })}
            preset={{ type: "user", id: userId }}
          />
        )}

        <div className="flex">
          <div className="flex-1">
            <Link
              to="/users/view/$userId"
              params={{ userId }}
              className="text-sm text-muted-foreground hover:underline"
            >
              ← Back to User
            </Link>
          </div>
          <div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Icon name="options" className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <Link
                  to="/charges/users/$userId"
                  params={{ userId }}
                  search={{ sheet: "grant" }}
                  className="text-sm text-primary hover:underline"
                >
                  <DropdownMenuItem>
                    <Icon name="person" className="h-4 w-4 mr-2" />
                    Grant Credits
                  </DropdownMenuItem>
                </Link>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div>
          <h2 className="text-xl font-semibold">{user.user.name} charge history</h2>
        </div>

        <ChargeHistorySection subject={{ userId }} />
      </div>
    </PermissionGuard>
  );
}
